import axios from 'axios';
import { Auth } from 'aws-amplify';
import {
  cookieToken,
  isPlayGroundMode,
  isAdminDashboard,
} from '../utils/CookieToken';

import getStore from '../store/store';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import {
  orgIdKey,
  defaultOrgIdValue,
  ORG_ID_PARAM,
} from '../components/widgets/OrgIdDropdown/utils';
import { orgIdHeaderKey } from '../constants/appConst';
import { LifelineAutoLogoutEvent } from '../analytics/login-logout/events';
import { MetricsForJumboEvent } from '../analytics/constants';
import { redirectToLogin } from '../utils/UtilFunctions';
import {
  getOrgIDFromSmartplugURL,
  getSmartplugsHeaders,
} from '../utils/UtilFunctionsV1';

const toastOptions = {
  id: 'api-status-429',
  duration: 5000,
  ariaProps: {
    role: 'status',
    'aria-live': 'polite',
  },
  className: 'custom-toast',
  style: {
    backgroundColor: '#ffedef',
    color: '#EF4F5F',
  },
  position: 'top-center',
  iconTheme: 'outlined',
};

const handleSignout = () => {
  // FIXME: this function has been written multiple time merge it
  Promise.all([
    Auth.currentAuthenticatedUser()
      .then(() => {
        return Auth.signOut();
      })
      .catch(() => {
        // do nothing
      }),
    fetch('/api/crm_proxy/lifeline_apis/auth/logout', {
      method: 'POST',
    }).then(() => {
      // send relative url with encoding as argument to redirectToLogin
      redirectToLogin(
        encodeURIComponent(
          `${window.location.pathname}${window.location.search}`,
        ),
      );
    }),
  ]);
};

export const Helpers = {};

let config = {
  mode: 'lifelineZ',
  url: `${window.location.origin}/api/crm_proxy/lifeline_apis`,
};

const getConfig = () => {
  let path = window.location.pathname;
  if (path.indexOf('lite-mode') > -1) {
    config.url = config.url.replace('/lifeline_apis', '');
    config.mode = 'lite-mode';
  }
  if (path.indexOf('handover') > -1) {
    config.mode = 'handover';
    return config;
  }
  if (path.indexOf('smartplugs/lifeline') > -1) {
    config.mode = 'lifeline';
    return config;
  }
  if (path.indexOf('help_desk') > -1) {
    config.mode = 'helpdesk';
    return config;
  }
  if (path.indexOf('smartplug') > -1) {
    config.mode = 'smartplug';
    return config;
  }
  return config;
};

getConfig();

window.dashboard = {
  mode: config.mode,
  domain: config.url,
};

window.__AUTH_DEV_ROUTE = `${window.dashboard.domain}/auth`;
export class Api {
  static getTDQueryParams = () => {
    //TD extend for traing dashboard
    const urlParams = new URLSearchParams(window.location.search);
    const key = urlParams.get('uc_training_option');
    return key;
  };

  static getAllQueryParams = () => {
    //TD extend for traing dashboard
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    return params;
  };

  static TDParam = this.getTDQueryParams();

  static baseURL = `${config.url}/${this.TDParam ? 'training' : ''}`;

  static axiosInstance = axios.create({
    baseURL: this.baseURL,
  });

  static serialize = (params) => {
    const queryString = [];
    const defaultParams = this.getAllQueryParams();
    // removes the org_id from the default params
    delete defaultParams[ORG_ID_PARAM];
    let mergedParams = {
      ...defaultParams,
      ...params,
      mode:
        'mode' in defaultParams ? defaultParams['mode'] : window.dashboard.mode,
    };

    Object.keys(mergedParams).forEach((key) => {
      const str = [
        encodeURIComponent(key),
        encodeURIComponent(mergedParams[key]),
      ];
      queryString.push(str.join('='));
    });

    return queryString.join('&');
  };

  static generateApiUrl = (url) => {
    const { queryParams = {}, path } = url;
    const queryString = this.serialize(queryParams);
    let finalUrlWithParams;
    if (queryString.length) {
      finalUrlWithParams = `${path}?${queryString}`;
    } else {
      finalUrlWithParams = path;
    }

    return finalUrlWithParams;
  };

  static post = (url, options = {}, headers, isDiffService = false, hostName) =>
    Api.send(
      url,
      {
        ...options,
        method: 'POST',
        headers: {
          ...headers,
        },
      },
      false,
      isDiffService,
      hostName,
    );

  static put = (url, options = {}, headers, isDiffService = false, hostName) =>
    Api.send(
      url,
      {
        ...options,
        method: 'PUT',
        headers: {
          ...headers,
        },
      },
      false,
      isDiffService,
      hostName,
    );

  static get = (url, options = {}, hasfetched = false) => {
    return Api.send(
      url,
      {
        ...options,
        method: 'GET',
      },
      hasfetched,
    );
  };

  static delete = (url, options = {}) =>
    Api.send(url, {
      ...options,
      method: 'DELETE',
    });

  static send = (url, options, isDiffService = false, hostName) => {
    if (isDiffService) {
      this.baseURL = hostName;
      this.axiosInstance = axios.create({
        baseURL: this.baseURL,
      });
    }

    if (isPlayGroundMode) {
      this.axiosInstance = axios.create({
        baseURL: this.baseURL.replace('/lifeline_apis', '/playground'),
      });
    }

    const finalUrlWithParams = this.generateApiUrl(url);
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return Promise.resolve(response);
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    const SmartplugOrgID = getOrgIDFromSmartplugURL();

    const smartplugHeaders = getSmartplugsHeaders();

    const orgIdHeaderValue = isAdminDashboard
      ? defaultOrgIdValue
      : url?.queryParams?.orgId
      ? url?.queryParams?.orgId
      : SmartplugOrgID
      ? SmartplugOrgID
      : sessionStorage.getItem(orgIdKey);

    let headers = {
      ...options.headers,
      'content-type': 'application/json',
      [orgIdHeaderKey]: orgIdHeaderValue,
      ...smartplugHeaders,
    };

    const { crm_user_id, email_id, name, external_user_id } =
      getStore()?.getState()?.authState?.userDetails || {};

    let token = getStore().getState().authState?.userDetails?.authToken;

    if (!cookieToken && token) {
      headers['cognito-access-token'] = token;
    }
    headers['app-client'] = 'lifeline_dashboard';

    return this.axiosInstance
      .request({
        ...options,
        url: finalUrlWithParams,
        withCredentials: true,
        headers,
        data: JSON.stringify(options.body),
      })
      .then((response) => {
        if (response?.status >= 200 && response?.status < 300) {
          return response.data;
        } else {
          return response;
        }
      })
      .catch((error) => {
        if (error?.response?.status === 401) {
          LifelineAutoLogoutEvent({
            [MetricsForJumboEvent.customer_id]: external_user_id,
            [MetricsForJumboEvent.feature_meta]: {
              agent_id: external_user_id,
              crm_user_id,
              email_id,
              name,
            },
          });
          return handleSignout();
        }

        if (error?.response?.status === 429) {
          // handle too many redirects
          Sentry.captureException(`Too many requests error : ${error}`);
          toast.error(
            "Slow down! You've exceeded the request limit. Please try again later.",
            toastOptions,
          );
        }

        if ([301, 302].indexOf(error?.response?.status) > -1) {
          console.error('please logout me');
          alert('please logout and try again');
        }

        const errorMessage =
          error?.response?.data?.message || 'Something went wrong';
        throw new Error(errorMessage);
      });
  };
}
